import { QueryClient } from '@tanstack/react-query';

import {
	EntityMetadataModel,
	ExpressionMetadataModel,
	ParameterMetadata,
	ParameterType,
	PrimitiveType,
	VariableSetMetadata,
	VariableTypeMetadata,
} from '../../../api/api';
import { interpolateTemplate } from '../../../common/interpolateTemplate';
import { sortByOrder } from '../../../common/sortByOrder';
import { FlowQueries } from '../Hooks/useFlowQueries';
import { ConstExpressionModel } from '../Models/ConstExpressionModel';
import { FlowRefModel, IntegrationEventRefModel, OperationRefModel, SchemaRefModel } from '../Models/DomainServiceModel';
import { ExpressionModel } from '../Models/ExpressionModel';
import { ExpressionTypes } from '../Models/ExpressionTypes';
import { FlowDefinitionModel } from '../Models/FlowModel';
import { JsonSchemaModel } from '../Models/JsonSchemaModel';
import { ObjectPropertyType } from '../Models/ObjectPropertyType';
import { SchemaValueBindingModel, SchemaValueModel } from '../Models/SchemaValueModel';
import { resolveFlowSchemaValue } from './FlowSchemaValues';
import { VarExpressionModel } from '../Models/VarExpressionModel';
import { CollectionVariableScope } from '../Models/VariableScopes/CollectionVariableScope';
import { IVariableScope } from '../Models/VariableScopes/IVariableScope';
import { SchemaTypeVariableScope } from '../Models/VariableScopes/SchemaTypeVariableScope';
import { VariableScope } from '../Models/VariableScopes/VariableScope';
import { convertJsonSchemaPropertyToEntityProperty, getEntityMetadataModelFromJsonSchema, getJsonSchemaFromProperty } from './EntityMetadataModelConverter';
import { getNonNullSchema, transformCollectionSchema } from './JsonSchemaTypes';
import { TypeRefSuffixes } from './TypeRefSuffixes';
import { projectVariableDictionaryKeys } from './VariableDictionaryKeysProjection';
import { resolveDependentDictionarySchema, resolveDependentSourceRef } from './DependentDictionarySchema';
import { getModelValueByNameRef, getServiceConnectionId } from './parameterValueOptions';

/**
 * Result of parsing a typeRef for suffix notation
 */
interface ParsedTypeRef {
	paramName: string;
	useRequestSchema?: boolean;
	useResponseSchema?: boolean;
	useInputSchema?: boolean;
	useOutputSchema?: boolean;
}

export abstract class VariableScopeCalculatorServiceBase {
	protected flowQueries: FlowQueries;
	protected queryClient: QueryClient;
	protected schemaValues: SchemaValueModel[];
	protected schemaEntityCache = new WeakMap<JsonSchemaModel, EntityMetadataModel>();

	private schemaEntity(schema: JsonSchemaModel): EntityMetadataModel {
		let entity = this.schemaEntityCache.get(schema);
		if (!entity) {
			entity = getEntityMetadataModelFromJsonSchema(schema);
			this.schemaEntityCache.set(schema, entity);
		}
		return entity;
	}

	constructor(flowQueries: FlowQueries, queryClient: QueryClient, schemaValues: SchemaValueModel[] = []) {
		this.flowQueries = flowQueries;
		this.queryClient = queryClient;
		this.schemaValues = schemaValues;
	}

	/**
	 * Parses a typeRef string to extract the parameter name and any suffix notation.
	 * Supports: .Request, .Response, .InputSchemaValue, .OutputSchemaValue
	 */
	protected parseTypeRefSuffix(typeRef: string): ParsedTypeRef {
		if (typeRef.endsWith(TypeRefSuffixes.Response)) {
			return {
				paramName: typeRef.substring(0, typeRef.length - TypeRefSuffixes.Response.length),
				useResponseSchema: true,
			};
		} else if (typeRef.endsWith(TypeRefSuffixes.Request)) {
			return {
				paramName: typeRef.substring(0, typeRef.length - TypeRefSuffixes.Request.length),
				useRequestSchema: true,
			};
		} else if (typeRef.endsWith(TypeRefSuffixes.InputSchemaValue)) {
			return {
				paramName: typeRef.substring(0, typeRef.length - TypeRefSuffixes.InputSchemaValue.length),
				useInputSchema: true,
			};
		} else if (typeRef.endsWith(TypeRefSuffixes.OutputSchemaValue)) {
			return {
				paramName: typeRef.substring(0, typeRef.length - TypeRefSuffixes.OutputSchemaValue.length),
				useOutputSchema: true,
			};
		}
		return { paramName: typeRef };
	}

	/**
	 * Resolves EntityMetadataModel from any ref-type parameter value.
	 * Handles: OperationRef, IntegrationEventRef, FlowRef, SchemaRef, SchemaValue.
	 *
	 * @param paramType The parameter type (OperationRef, SchemaRef, etc.)
	 * @param refValue The actual ref value from the model
	 * @param options Optional flags for schema selection (request/response, input/output)
	 * @returns The resolved EntityMetadataModel, or undefined if not resolvable
	 */
	protected async resolveEntityFromRefType(
		paramType: ParameterType,
		refValue: any,
		options?: {
			useRequestSchema?: boolean;
			useResponseSchema?: boolean;
			useInputSchema?: boolean;
			useOutputSchema?: boolean;
		},
	): Promise<EntityMetadataModel | undefined> {
		if (!refValue) return undefined;

		try {
			if (paramType === ParameterType.OperationRef) {
				const operation = await this.queryClient.fetchQuery(this.flowQueries.queryOperationByRef(refValue as OperationRefModel));
				if (operation) {
					// For operations: default to request for input context, response for output context
					if (options?.useRequestSchema) {
						return operation.request ?? undefined;
					} else {
						// Default or explicit response
						return operation.response ?? undefined;
					}
				}
			} else if (paramType === ParameterType.IntegrationEventRef) {
				const integrationEvent = await this.queryClient.fetchQuery(this.flowQueries.queryIntegrationEventByRef(refValue as IntegrationEventRefModel));
				return integrationEvent?.payload ?? undefined;
			} else if (paramType === ParameterType.FlowRef) {
				const flowRef = refValue as FlowRefModel;
				if (flowRef?.Id) {
					const flowViewModel = await this.queryClient.fetchQuery(this.flowQueries.queryFlowById(flowRef));
					if (flowViewModel?.definition) {
						const flowDefinition = JSON.parse(flowViewModel.definition) as FlowDefinitionModel;
							const schemaValue = resolveFlowSchemaValue(flowDefinition, options?.useOutputSchema ? flowDefinition.OutputSchemaValue : flowDefinition.InputSchemaValue);
						if (schemaValue?.Value) {
							return this.schemaEntity(schemaValue.Value);
						}
					}
				}
			} else if (paramType === ParameterType.SchemaRef) {
				return await this.getEntityMetadataFromSchemaRef(refValue as SchemaRefModel);
			} else if (paramType === ParameterType.SchemaValue) {
				const schemaValue = resolveFlowSchemaValue({ SchemaValues: this.schemaValues }, refValue as SchemaValueBindingModel);
				if (schemaValue?.Value) {
					return this.schemaEntity(schemaValue.Value);
				}
			}
		} catch (error) {
			console.error(`Error resolving entity from ${paramType}:`, error);
		}

		return undefined;
	}

	/**
	 * Fetches entity metadata from a SchemaRef
	 */
	protected async getEntityMetadataFromSchemaRef(schemaRef: SchemaRefModel | undefined): Promise<EntityMetadataModel | undefined> {
		if (!schemaRef || !schemaRef.Id) {
			return undefined;
		}

		try {
			const cachedEntities = this.queryClient.getQueryData<EntityMetadataModel[]>(this.flowQueries.entitySchemasOfContextQueryKey(schemaRef.ContextId));
			const cachedEntity = cachedEntities?.find((entity) => entity.id === schemaRef.Id);
			if (cachedEntity) {
				return cachedEntity;
			}

			const schema = await this.queryClient.fetchQuery(this.flowQueries.queryEntitySchemaByRef(schemaRef, false));
			return schema || undefined;
		} catch (error) {
			console.error('Error fetching schema from SchemaRef:', error);
			return undefined;
		}
	}

	/**
	 * Resolves a variable name from nameRef, which can be either a string or a VarExpression.
	 * Returns only the root name (Value[0] for Var expressions).
	 * @param nameRefValue The value from the activity/expression parameter at nameRef
	 * @returns The resolved variable name or undefined
	 */
	protected resolveVariableNameFromNameRef(nameRefValue: any): string | undefined {
		return this.resolveNameRefFullPath(nameRefValue)?.[0];
	}

	/**
	 * Analyzes an expression to determine its result type using metadata
	 * This method is shared between Activity and Expression variable scope calculators
	 * @param expression The expression to analyze
	 * @param metadata The expression metadata
	 * @param variableScope The current variable scope
	 * @param name The name for the resulting property type
	 * @param variable Optional variable metadata for collection wrapping
	 * @param getPropertyTypeFromExpression Callback to recursively analyze nested expressions
	 * @param getVariableTypeAsPrimitive Callback to create a primitive property type
	 */
	protected analyzeExpressionResultType(
		expression: ExpressionModel,
		metadata: ExpressionMetadataModel,
		variableScope: IVariableScope,
		name: string,
		variable: VariableTypeMetadata | undefined,
		getPropertyTypeFromExpression: (
			expression: ExpressionModel,
			name: string,
			variableScope: IVariableScope,
			variable?: VariableTypeMetadata,
		) => ObjectPropertyType | undefined,
		getVariableTypeAsPrimitive: (name: string, type: PrimitiveType, variable?: VariableTypeMetadata) => ObjectPropertyType,
	): ObjectPropertyType | undefined {
		// If the expression metadata has a direct result type, use it
		if (metadata.resultType?.typeJsonSchema && !metadata.resultType.typeRef) {
			return convertJsonSchemaPropertyToEntityProperty(name, metadata.resultType.typeJsonSchema, true);
		}

		if (metadata.resultType?.type) {
			const variableType = getVariableTypeAsPrimitive(name, metadata.resultType.type, variable);
			// Apply wrapping/unwrapping if specified (though rare with direct types)
			if (metadata.resultType) {
				return this.applyCollectionWrapping(variableType, metadata.resultType.typeWrapInCollection, metadata.resultType.typeUnwrapFromCollection);
			}
			return variableType;
		}

		// If there's a resultTypeRef, follow it to determine the type
		if (metadata.resultType?.typeRef) {
			const [parameterName, ...memberPath] = metadata.resultType.typeRef.split('.');
			const referencedParam = metadata.parameters.find((param: ParameterMetadata) => param.name === parameterName);
			if (referencedParam) {
				const paramValue = expression[referencedParam.name];
				if (paramValue && referencedParam.type === ParameterType.Expression) {
					const nestedExpression = paramValue as ExpressionModel;
					const analysisScope = referencedParam.expectedType?.variableSets?.length ? new VariableScope() : variableScope;
					if (analysisScope !== variableScope) analysisScope.addPrevious(variableScope as VariableScope);
					for (const declaration of referencedParam.expectedType?.variableSets ?? []) {
						if (!declaration.name || !declaration.typeRef) continue;
						const source = expression[declaration.typeRef];
						if (!source?.Type) continue;
						const type = getPropertyTypeFromExpression(source, declaration.name, variableScope, declaration);
						const resolved = this.applyCollectionWrapping(type, declaration.typeWrapInCollection, declaration.typeUnwrapFromCollection);
						if (resolved) analysisScope.setVariable(declaration.name, resolved);
					}
					let resultType = getPropertyTypeFromExpression(nestedExpression, name, analysisScope, variable);
					for (const member of memberPath) {
						resultType = resultType?.referenceEntity?.properties.find((property) => property.name === member);
					}

					// Apply collection wrapping/unwrapping based on resultType metadata
					if (metadata.resultType) {
						resultType = this.applyCollectionWrapping(resultType, metadata.resultType.typeWrapInCollection, metadata.resultType.typeUnwrapFromCollection);
						if (resultType) {
							resultType = { ...resultType, name: name };
							if (metadata.resultType.typeJsonSchema?.['x-allowsNull']) {
								resultType = convertJsonSchemaPropertyToEntityProperty(name, { anyOf: [getJsonSchemaFromProperty(resultType), { type: 'null' }] }, !!resultType.isRequired);
							}
						}
					}
					return resultType;
				}
			}
		}

		return undefined;
	}

	/**
	 * Applies collection wrapping or unwrapping to a variable type.
	 * Supports nested collections using isMultiple + isMultipleNested flags.
	 *
	 * Collection nesting levels:
	 * - T: isMultiple=false, isMultipleNested=false/undefined
	 * - Collection<T>: isMultiple=true, isMultipleNested=false/undefined
	 * - Collection<Collection<T>>: isMultiple=true, isMultipleNested=true
	 *
	 * @param variableType The type to wrap/unwrap
	 * @param wrap If true, adds one collection nesting level
	 * @param unwrap If true, removes one collection nesting level
	 * @returns The wrapped/unwrapped type, or undefined if input is undefined
	 */
	protected applyCollectionWrapping(
		variableType: ObjectPropertyType | undefined,
		wrap: boolean | undefined,
		unwrap: boolean | undefined,
	): ObjectPropertyType | undefined {
		if (!variableType) {
			return undefined;
		}
		if (variableType.jsonSchema && (wrap || unwrap)) {
			const schema = transformCollectionSchema(variableType.jsonSchema, wrap, unwrap);
			const result = convertJsonSchemaPropertyToEntityProperty(variableType.name, schema, !!variableType.isRequired);
			const nonNull = getNonNullSchema(schema);
			return { ...result, isMultipleNested: nonNull.type === 'array' && !!nonNull.items && !Array.isArray(nonNull.items) && nonNull.items.type === 'array' };
		}

		let result = { ...variableType };

		// Unwrap: Remove one level of collection nesting
		if (unwrap) {
			if (result.jsonSchema?.type === 'array' && result.jsonSchema.items && !Array.isArray(result.jsonSchema.items)) {
				result.jsonSchema = result.jsonSchema.items;
			}
			if (result.isMultiple && result.isMultipleNested) {
				// Collection<Collection<T>> -> Collection<T>
				result.isMultipleNested = false;
			} else if (result.isMultiple) {
				// Collection<T> -> T
				result.isMultiple = false;
				result.isMultipleNested = undefined;
			}
			// If not a collection, unwrap has no effect
		}

		// Wrap: Add one level of collection nesting
		if (wrap) {
			if (result.jsonSchema) result.jsonSchema = { type: 'array', items: result.jsonSchema };
			if (!result.isMultiple) {
				// T -> Collection<T>
				result.isMultiple = true;
				result.isMultipleNested = false;
			} else if (!result.isMultipleNested) {
				// Collection<T> -> Collection<Collection<T>>
				result.isMultipleNested = true;
			}
			// If already nested collection, wrap has no effect (max 2 levels)
		}

		return result;
	}

	/**
	 * Helper method to wrap/unwrap collection types based on variable metadata
	 */
	protected getVariableTypeCollectionWrapping(variableType: ObjectPropertyType | undefined, variable: VariableTypeMetadata) {
		return this.applyCollectionWrapping(variableType, variable.typeWrapInCollection, variable.typeUnwrapFromCollection);
	}

	/**
	 * Resolves the property type for a Const expression, inferring PrimitiveType
	 * from the raw JavaScript value when ValueType is not explicitly set.
	 * Returns undefined if the type cannot be determined (e.g. null/undefined value).
	 */
	protected resolveConstExpressionType(constExp: ConstExpressionModel, name: string): ObjectPropertyType | undefined {
		const value = constExp.Value;
		let valueType = constExp.ValueType;
		if (!valueType) {
			if (typeof value === 'boolean') valueType = PrimitiveType.Boolean;
			else if (typeof value === 'string') valueType = PrimitiveType.String;
			else if (typeof value === 'number') valueType = Number.isInteger(value) ? PrimitiveType.Integer : PrimitiveType.Decimal;
		}
		return valueType ? this.getVariableTypeAsPrimitive(name, valueType) : undefined;
	}

	/**
	 * Resolves the property type that a Var expression refers to by walking
	 * the variable scope chain. Returns the property type with the given name,
	 * or undefined if the path cannot be resolved.
	 */
	protected resolveVarExpressionType(varExp: VarExpressionModel, variableScope: IVariableScope, name: string): ObjectPropertyType | undefined {
		if (!varExp.Value || varExp.Value.length === 0) {
			return undefined;
		}

		let currentVarType: ObjectPropertyType | undefined = undefined;
		let currentVariableScope: IVariableScope = variableScope;

		for (let i = 0; i < varExp.Value.length; i++) {
			const currentVarName = varExp.Value[i];
			currentVarType = currentVariableScope.getVariable(currentVarName);
			if (!currentVarType) {
				return undefined;
			}
			if (currentVarType.referenceEntity) {
				currentVariableScope = currentVarType.isMultiple
					? new CollectionVariableScope(currentVarType.referenceEntity)
					: new SchemaTypeVariableScope(currentVarType.referenceEntity);
			}
		}

		return currentVarType ? { ...currentVarType, name } : undefined;
	}

	/**
	 * Converts a resolved property type into a variable scope for navigating into
	 * its properties. Returns undefined for primitive/unresolved types.
	 */
	protected getVariableScopeFromPropertyType(propertyType: ObjectPropertyType | undefined): IVariableScope | undefined {
		if (!propertyType?.referenceEntity) {
			return undefined;
		}
		return propertyType.isMultiple ? new CollectionVariableScope(propertyType.referenceEntity) : new SchemaTypeVariableScope(propertyType.referenceEntity);
	}

	/**
	 * Helper method to create a primitive property type
	 */
	protected getVariableTypeAsPrimitive(name: string, type: PrimitiveType): ObjectPropertyType {
		return {
			name: name,
			type: type,
			isComplex: false,
			isMultiple: false,
			isRequired: false,
		} as ObjectPropertyType;
	}

	/**
	 * Helper method to create a schema property type
	 */
	protected getVariableTypeAsSchema(name: string, type: EntityMetadataModel): ObjectPropertyType {
		return {
			name: name,
			type: PrimitiveType.Object,
			isComplex: true,
			isMultiple: false,
			isRequired: false,
			referenceEntity: type,
			referenceEntityId: type.id,
		} as ObjectPropertyType;
	}

	/**
	 * Resolves the expected object type (EntityMetadataModel) for an Expression parameter
	 * whose expectedType.typeRef points to a sibling ref-type parameter (OperationRef,
	 * IntegrationEventRef, FlowRef, SchemaRef, SchemaValue).
	 *
	 * This is the *input* schema variant — it answers "what object shape does this expression
	 * need to produce?" — as opposed to processVariableSets which resolves output/response schemas.
	 *
	 * Supports special syntax: "ParameterName.Request" or "ParameterName.Response" to get specific schema from an OperationRef.
	 *
	 * @param parameterMetadata The Expression parameter whose typeRef we want to follow
	 * @param parameters All sibling parameters (to locate the referenced one)
	 * @param data The current model/expression data (to read the ref value)
	 * @returns The resolved EntityMetadataModel, or undefined if not resolvable
	 */
	public async resolveExpectedObjectType(
		parameterMetadata: ParameterMetadata,
		parameters: ParameterMetadata[],
		data: Record<string, any>,
	): Promise<EntityMetadataModel | undefined> {
		if (!parameterMetadata.expectedType?.typeRef) {
			return undefined;
		}

		const parsed = this.parseTypeRefSuffix(parameterMetadata.expectedType.typeRef);
		const referencedParam = parameters.find((p) => p.name === parsed.paramName);
		if (!referencedParam) {
			return undefined;
		}

		const refValue = data[parsed.paramName];
		if (!refValue) {
			return undefined;
		}

		// For input schema context: default to request, allow explicit response
		const options = {
			useRequestSchema: parsed.useRequestSchema ?? !parsed.useResponseSchema,
			useResponseSchema: parsed.useResponseSchema,
			useInputSchema: parsed.useInputSchema,
			useOutputSchema: parsed.useOutputSchema,
		};

		return this.resolveEntityFromRefType(referencedParam.type, refValue, options);
	}

	/**
	 * Returns the metadata for the named expression type, or undefined.
	 * Subclasses that own an expression metadata list must override this so that
	 * resolveExpressionResultTypeAsync can follow resultType.typeRef chains.
	 */
	protected getExpressionMetadata(_expressionType: string): ExpressionMetadataModel | undefined {
		return undefined;
	}

	/**
	 * Asynchronously resolves the result type of an expression by following its
	 * metadata's resultType.typeRef chain to an OperationRef, SchemaRef, FlowRef, etc.
	 * Called when the synchronous getPropertyTypeFromExpression callback returns undefined
	 * for an Expression-typed typeRef parameter.
	 *
	 * Supports special syntax: "ParameterName.Request" or "ParameterName.Response" to get specific schema from an OperationRef.
	 * When the referenced parameter has variableSets (e.g., $source in ObjectMap.Map), those variables
	 * are made available when recursively resolving the parameter's expression type.
	 */
	private async resolveExpressionResultTypeAsync(
		expression: ExpressionModel,
		name: string,
		inputScope?: IVariableScope,
	): Promise<ObjectPropertyType | undefined> {
		const metadata = this.getExpressionMetadata(expression.Type);
		if (!metadata?.resultType?.typeRef) return undefined;

		const parsed = this.parseTypeRefSuffix(metadata.resultType.typeRef);
		const resultParam = metadata.parameters.find((p) => p.name === parsed.paramName);
		if (!resultParam) return undefined;

		const paramValue = expression[parsed.paramName];
		if (!paramValue) return undefined;

		// Now resolve the parameter's type
		if (resultParam.type === ParameterType.Expression) {
			const nestedExpression = paramValue as ExpressionModel;

			// If the parameter has variableSets and the expression is a Var, resolve it with augmented scope
			// This handles cases like ObjectMap.Map where $source needs to be available
			if (resultParam.expectedType?.variableSets?.length && nestedExpression.Type === ExpressionTypes.Var && inputScope) {
				// Create augmented scope with variables from parameter-level variableSets
				const augmentedScope = new VariableScope();
				augmentedScope.addPrevious(inputScope as VariableScope);

				// Process typeRef-based variableSets (e.g., $source pointing to Object parameter)
				for (const varSet of resultParam.expectedType.variableSets) {
					if (varSet.name && varSet.typeRef) {
						const sourceParam = metadata.parameters.find((p) => p.name === varSet.typeRef);
						if (sourceParam && sourceParam.type === ParameterType.Expression) {
							const sourceValue = expression[sourceParam.name];
							if (sourceValue) {
								// Recursively resolve source expression type
								const sourceType = await this.resolveExpressionTypeRecursive(sourceValue as ExpressionModel, varSet.name, inputScope);
								if (sourceType) {
									augmentedScope.setVariable(varSet.name, sourceType);
								}
							}
						}
					}
				}

				// Now resolve the Var expression with the augmented scope
				const resolvedType = this.resolveVarExpressionType(nestedExpression as VarExpressionModel, augmentedScope, name);
				if (resolvedType && metadata.resultType) {
					return this.applyCollectionWrapping(resolvedType, metadata.resultType.typeWrapInCollection, metadata.resultType.typeUnwrapFromCollection);
				}
				return resolvedType;
			}

			// For other expression types, recursively resolve their type
			const resolvedType = await this.resolveExpressionTypeRecursive(nestedExpression, name, inputScope);

			// Apply the expression-level resultType collection wrapping before returning
			if (metadata.resultType && resolvedType) {
				return this.applyCollectionWrapping(resolvedType, metadata.resultType.typeWrapInCollection, metadata.resultType.typeUnwrapFromCollection);
			}

			return resolvedType;
		}

		// For ref-type parameters (OperationRef, SchemaRef, etc.)
		const options = {
			useRequestSchema: parsed.useRequestSchema,
			useResponseSchema: parsed.useResponseSchema ?? !parsed.useRequestSchema,
			useOutputSchema: parsed.useOutputSchema ?? !parsed.useInputSchema,
			useInputSchema: parsed.useInputSchema,
		};

		const entity = await this.resolveEntityFromRefType(resultParam.type, paramValue, options);
		if (!entity) return undefined;

		let varType: ObjectPropertyType | undefined = this.getVariableTypeAsSchema(name, entity);

		// Apply the expression-level resultType collection wrapping before returning.
		// The caller (processVariableSets) will then apply the variableSet's own wrapping on top.
		if (metadata.resultType && varType) {
			varType = this.applyCollectionWrapping(varType, metadata.resultType.typeWrapInCollection, metadata.resultType.typeUnwrapFromCollection);
		}

		return varType;
	}

	/**
	 * Recursively resolves the type of an expression by traversing nested expressions
	 * using metadata to discover which parameters contain sub-expressions.
	 *
	 * Strategy:
	 * 1. If metadata has resultType.typeRef to an Expression parameter, traverse that parameter
	 * 2. Otherwise, find all Expression-type parameters and try resolving types from them
	 * 3. For array parameters (e.g., And.Value, Or.Value), try resolving from last to first
	 * 4. Return the first resolvable type found
	 * 5. Fall back to resolveExpressionResultTypeAsync for expressions with ref-type parameters
	 *
	 * This approach handles both:
	 * - Expressions with explicit resultType.typeRef (e.g., operations referencing other params)
	 * - Composite expressions (And, Or) where result type is inferred from nested expressions
	 */
	private async resolveExpressionTypeRecursive(
		expression: ExpressionModel,
		name: string,
		inputScope?: IVariableScope,
	): Promise<ObjectPropertyType | undefined> {
		// Get metadata to discover parameters
		const metadata = this.getExpressionMetadata(expression.Type);
		if (!metadata) {
			return this.resolveExpressionResultTypeAsync(expression, name, inputScope);
		}

		// Strategy 1: If metadata explicitly specifies which parameter determines the result type via typeRef
		if (metadata.resultType?.typeRef) {
			const resultParam = metadata.parameters.find((p) => p.name === metadata.resultType!.typeRef);

			if (resultParam?.type === ParameterType.Expression) {
				const paramValue = expression[resultParam.name];

				// If the parameter has variableSets and inputScope exists, create augmented scope
				// This handles cases like ObjectMap.Map where $source needs to be available
				if (resultParam.expectedType?.variableSets?.length && inputScope) {
					const nestedExpression = paramValue as ExpressionModel;

					// For Var and Object expressions, resolve with augmented scope including parameter variables
					if (nestedExpression?.Type === ExpressionTypes.Var || nestedExpression?.Type === ExpressionTypes.Object) {
						const augmentedScope = new VariableScope();
						augmentedScope.addPrevious(inputScope as VariableScope);

						// Process typeRef-based variableSets (e.g., $source pointing to Object parameter)
						for (const varSet of resultParam.expectedType.variableSets) {
							if (varSet.name && varSet.typeRef) {
								const sourceParam = metadata.parameters.find((p) => p.name === varSet.typeRef);
								if (sourceParam && sourceParam.type === ParameterType.Expression) {
									const sourceValue = expression[sourceParam.name];
									if (sourceValue) {
										// Recursively resolve source expression type
										const sourceType = await this.resolveExpressionTypeRecursive(sourceValue as ExpressionModel, varSet.name, inputScope);
										if (sourceType) {
											augmentedScope.setVariable(varSet.name, sourceType);
										}
									}
								}
							}
						}

						// Resolve the expression with the augmented scope
						if (nestedExpression.Type === ExpressionTypes.Var) {
							const resolvedType = this.resolveVarExpressionType(nestedExpression as VarExpressionModel, augmentedScope, name);
							if (resolvedType && metadata.resultType) {
								// Apply collection wrapping from metadata.resultType if specified
								return this.applyCollectionWrapping(resolvedType, metadata.resultType.typeWrapInCollection, metadata.resultType.typeUnwrapFromCollection);
							}
							return resolvedType;
						} else if (nestedExpression.Type === ExpressionTypes.Object) {
							// For Object expressions, build the object type from properties
							// This requires analyzing each property expression with the augmented scope
							// For now, fall through to standard resolution
						}
					}
				}

				// Standard resolution for expressions without variableSets or non-Var/Object types
				const resolvedType = await this.tryResolveFromExpressionParameter(paramValue, name, inputScope);
				if (resolvedType) {
					return resolvedType;
				}
			}
		}

		// Strategy 2: No explicit typeRef - try to infer from any Expression-type parameters
		// This handles composite expressions like And/Or where the result type comes from nested expressions
		const expressionParams = metadata.parameters.filter((p) => p.type === ParameterType.Expression);
		for (const param of expressionParams) {
			const paramValue = expression[param.name];
			const resolvedType = await this.tryResolveFromExpressionParameter(paramValue, name, inputScope);
			if (resolvedType) {
				return resolvedType;
			}
		}

		// Strategy 3: Fall back to standard resolution for expressions with ref-type parameters
		return this.resolveExpressionResultTypeAsync(expression, name, inputScope);
	}

	/**
	 * Helper method to try resolving a type from an expression parameter value.
	 * Handles both single expressions and arrays of expressions.
	 * For arrays, tries resolving from last to first (works for And-like semantics).
	 */
	private async tryResolveFromExpressionParameter(paramValue: any, name: string, inputScope?: IVariableScope): Promise<ObjectPropertyType | undefined> {
		if (!paramValue) {
			return undefined;
		}

		// Handle array of expressions (e.g., And.Value, Or.Value)
		if (Array.isArray(paramValue) && paramValue.length > 0) {
			// Try resolving from last to first (works for And-like semantics where last value determines type)
			for (let i = paramValue.length - 1; i >= 0; i--) {
				const nestedExpr = paramValue[i];
				if (nestedExpr?.Type) {
					const resolvedType = await this.resolveExpressionTypeRecursive(nestedExpr, name, inputScope);
					if (resolvedType) {
						return resolvedType;
					}
				}
			}
		}
		// Handle single nested expression
		else if (paramValue.Type) {
			return this.resolveExpressionTypeRecursive(paramValue, name, inputScope);
		}

		return undefined;
	}

	/**
	 * Resolves variable type from an assignable expression parameter (like ListSelect.Item).
	 * Uses the parameter's expectedType to define the variable's type, rather than
	 * evaluating the expression's current value.
	 */
	private async resolveAssignableExpressionType(
		variableName: string,
		variable: VariableSetMetadata,
		typeRefParameter: ParameterMetadata,
		parameters: ParameterMetadata[],
		data: Record<string, any>,
		inputScope: IVariableScope,
		getPropertyTypeFromExpression?: (
			expression: ExpressionModel,
			name: string,
			variableScope: IVariableScope,
			variable?: VariableTypeMetadata,
		) => ObjectPropertyType | undefined,
	): Promise<ObjectPropertyType | undefined> {
		const expectedType = typeRefParameter.expectedType;
		if (!expectedType) return undefined;

		// Case 1: expectedType has variableSets - process them to get the type
		if (expectedType.variableSets?.length) {
			const paramExpectedScope = new VariableScope();
			paramExpectedScope.addPrevious(inputScope as VariableScope);
			await this.processVariableSets(
				expectedType.variableSets,
				parameters,
				data,
				inputScope,
				paramExpectedScope,
				`expectedType of parameter '${typeRefParameter.name}'`,
				getPropertyTypeFromExpression,
			);
			const expectedVars = paramExpectedScope.getVariables();
			const firstEntry = expectedVars.entries().next();
			if (!firstEntry.done) {
				const expectedVarType = firstEntry.value[1];
				if (expectedVarType) {
					let varType: ObjectPropertyType = { ...expectedVarType, name: variableName };
					return this.getVariableTypeCollectionWrapping(varType, variable);
				}
			}
		}

		// Case 2: expectedType has a direct typeRef (e.g., "Items") - resolve it
		if (expectedType.typeRef) {
			const referencedParam = parameters.find((x) => x.name === expectedType.typeRef);
			const referencedValue = referencedParam ? data[expectedType.typeRef] : undefined;
			if (referencedValue && referencedParam?.type === ParameterType.Expression) {
				const referencedExpression = referencedValue as ExpressionModel;

				// Try sync callback first
				if (getPropertyTypeFromExpression) {
					const mergedMetadata = { ...variable, ...expectedType };
					const resolvedType = getPropertyTypeFromExpression(referencedExpression, variableName, inputScope, mergedMetadata);
					if (resolvedType) {
						return resolvedType;
					}
				}

				// Fall back to async resolution
				const asyncType = await this.resolveExpressionTypeRecursive(referencedExpression, variableName, inputScope);
				if (asyncType) {
					const mergedMetadata = { ...variable, ...expectedType };
					return this.getVariableTypeCollectionWrapping(asyncType, mergedMetadata);
				}
			}
		}

		return undefined;
	}

	/**
	 * Resolves variable type from an expression parameter.
	 * Tries synchronous analysis first, then async, then Var-specific scope resolution.
	 */
	private async resolveExpressionParameterType(
		variableName: string,
		variable: VariableSetMetadata,
		expression: ExpressionModel,
		typeRefParameter: ParameterMetadata,
		parameters: ParameterMetadata[],
		data: Record<string, any>,
		inputScope: IVariableScope,
		getPropertyTypeFromExpression?: (
			expression: ExpressionModel,
			name: string,
			variableScope: IVariableScope,
			variable?: VariableTypeMetadata,
		) => ObjectPropertyType | undefined,
	): Promise<ObjectPropertyType | undefined> {
		// Try synchronous analysis first
		if (getPropertyTypeFromExpression) {
			const syncType = getPropertyTypeFromExpression(expression, variableName, inputScope, variable);
			if (syncType) {
				return this.getVariableTypeCollectionWrapping(syncType, variable);
			}
		}

		// Async analysis for expressions requiring data fetches (e.g., OperationCall)
		const asyncType = await this.resolveExpressionResultTypeAsync(expression, variableName, inputScope);
		if (asyncType) {
			return this.getVariableTypeCollectionWrapping(asyncType, variable);
		}

		// Special handling for Var expressions: resolve within parameter's own scope
		if (expression.Type === ExpressionTypes.Var) {
			const paramScope = new VariableScope();
			paramScope.addPrevious(inputScope as VariableScope);
			if (typeRefParameter.expectedType?.variableSets?.length) {
				await this.processVariableSets(
					typeRefParameter.expectedType.variableSets,
					parameters,
					data,
					inputScope,
					paramScope,
					`parameter scope for '${typeRefParameter.name}'`,
					getPropertyTypeFromExpression,
				);
			}
			// Keep the caller's Target-aware analysis when retrying with property-local variables.
			const resolved = getPropertyTypeFromExpression
				? getPropertyTypeFromExpression(expression, variableName, paramScope, variable)
				: this.resolveVarExpressionType(expression as VarExpressionModel, paramScope, variableName);
			if (resolved) {
				return this.getVariableTypeCollectionWrapping(resolved, variable);
			}
		}

		return undefined;
	}

	/**
	 * Resolves variable type from a typeRef pointing to a parameter.
	 * Handles assignable expressions, regular expressions, and ref-type parameters.
	 */
	private async resolveVariableTypeFromTypeRef(
		variableName: string,
		variable: VariableSetMetadata,
		typeRefParameter: ParameterMetadata,
		refValue: any,
		parsed: ParsedTypeRef,
		parameters: ParameterMetadata[],
		data: Record<string, any>,
		inputScope: IVariableScope,
		contextName: string,
		getPropertyTypeFromExpression?: (
			expression: ExpressionModel,
			name: string,
			variableScope: IVariableScope,
			variable?: VariableTypeMetadata,
		) => ObjectPropertyType | undefined,
	): Promise<ObjectPropertyType | undefined> {
		// Special handling for assignable expression parameters
		if (typeRefParameter.type === ParameterType.Expression && typeRefParameter.expectedExpressionIsAssignable) {
			return this.resolveAssignableExpressionType(variableName, variable, typeRefParameter, parameters, data, inputScope, getPropertyTypeFromExpression);
		}

		if (!refValue) return undefined;

		try {
			// Handle expression parameters
			if (typeRefParameter.type === ParameterType.Expression) {
				return this.resolveExpressionParameterType(
					variableName,
					variable,
					refValue as ExpressionModel,
					typeRefParameter,
					parameters,
					data,
					inputScope,
					getPropertyTypeFromExpression,
				);
			}

			// Handle ref-type parameters (OperationRef, SchemaRef, etc.)
			const options = {
				useRequestSchema: parsed.useRequestSchema,
				useResponseSchema: parsed.useResponseSchema ?? !parsed.useRequestSchema,
				useInputSchema: parsed.useInputSchema,
				useOutputSchema: parsed.useOutputSchema ?? !parsed.useInputSchema,
			};

			const entity = await this.resolveEntityFromRefType(typeRefParameter.type, refValue, options);
			if (entity) {
				let varType = this.getVariableTypeAsSchema(variableName, entity);
				return this.getVariableTypeCollectionWrapping(varType, variable);
			}
		} catch (error) {
			console.error(`Error resolving type for variable '${variableName}' in ${contextName}:`, error);
		}

		return undefined;
	}

	/**
	 * Process variable sets from activity or expression metadata
	 * This shared method handles the common logic for resolving variable names and types
	 */
	public async processVariableSets(
		variableSets: VariableSetMetadata[] | undefined,
		parameters: ParameterMetadata[],
		data: Record<string, any>,
		inputScope: IVariableScope,
		outputScope: VariableScope,
		contextName: string,
		getPropertyTypeFromExpression?: (
			expression: ExpressionModel,
			name: string,
			variableScope: IVariableScope,
			variable?: VariableTypeMetadata,
		) => ObjectPropertyType | undefined,
	): Promise<void> {
		// Sort by order to ensure variables are processed in the correct sequence
		const sortedVariableSets = variableSets ? [...variableSets].sort(sortByOrder) : [];

		for (const variable of sortedVariableSets) {
			// Resolve variable name
			let variableName: string | undefined = undefined;
			if (variable.name) {
				variableName = variable.name.includes('{{') ? interpolateTemplate(variable.name, data, true) : variable.name;
			}
			if (variable.nameRef) {
				variableName = this.resolveVariableNameFromNameRef(data[variable.nameRef]);
			}

			if (!variableName) continue;

			// Resolve variable type
			let variableType: ObjectPropertyType | undefined = undefined;

			// Dynamic JSON Schema type
			if (variable.typeJsonSchemaSource && !variableType) {
				const entityMetadata = await this.resolveServiceConnectionPropertyJsonSchema(variable.typeJsonSchemaSource, data);
				if (entityMetadata) {
					variableType = this.getVariableTypeAsSchema(variableName, entityMetadata);
					variableType = this.getVariableTypeCollectionWrapping(variableType, variable);
				}
			}

			// Static type
			if (variable.type && !variableType && !variable.typeJsonSchema) {
				variableType = this.getVariableTypeAsPrimitive(variableName, variable.type);
				variableType = this.getVariableTypeCollectionWrapping(variableType, variable);
			}

			// JSON Schema type
			if (variable.typeJsonSchema && !variableType && !variable.typeRef) {
				variableType = convertJsonSchemaPropertyToEntityProperty(variableName, variable.typeJsonSchema as JsonSchemaModel, true);
				if (getNonNullSchema(variable.typeJsonSchema).type !== 'array') {
					variableType = this.getVariableTypeCollectionWrapping(variableType, variable);
				}
			}

			// TypeRef (references another parameter)
			if (variable.typeRef && !variableType) {
				const parsed = this.parseTypeRefSuffix(variable.typeRef);
				const typeRefParameter = parameters.find((x) => x.name === parsed.paramName);

				if (!typeRefParameter) {
					outputScope.addDiagnostic(`Cannot resolve ${variableName}: declaration in ${contextName} references missing parameter '${parsed.paramName}'.`);
					continue;
				}

				const refValue = data[parsed.paramName];
				if (typeRefParameter.type === ParameterType.SchemaValue && refValue?.SchemaValueId
					&& !resolveFlowSchemaValue({ SchemaValues: this.schemaValues }, refValue)) {
					outputScope.addDiagnostic(`Cannot resolve ${variableName}: shared schema '${refValue.SchemaValueId}' referenced by ${contextName}.${parsed.paramName} is missing.`);
				}
				variableType = await this.resolveVariableTypeFromTypeRef(
					variableName,
					variable,
					typeRefParameter,
					refValue,
					parsed,
					parameters,
					data,
					inputScope,
					contextName,
					getPropertyTypeFromExpression,
				);
				if (!variableType && refValue && typeRefParameter.type !== ParameterType.Expression
					&& !(refValue.SchemaValueId && !resolveFlowSchemaValue({ SchemaValues: this.schemaValues }, refValue))) {
					outputScope.addDiagnostic(`Cannot resolve ${variableName} declared by ${contextName}: check '${parsed.paramName}' and its referenced schema metadata.`);
				}
			}

			if (variableName && variableType) {
				if (variable.typeRef && variable.typeJsonSchema?.['x-allowsNull']) {
					variableType = { ...variableType, jsonSchema: { anyOf: [getJsonSchemaFromProperty(variableType), { type: 'null' }] } };
				}

				const projectedSchema = resolveDependentDictionarySchema(getJsonSchemaFromProperty(variableType), sourceRef =>
					resolveDependentSourceRef(data, sourceRef, expression =>
						getPropertyTypeFromExpression?.(expression, '$source', inputScope)));
				if ((getJsonSchemaFromProperty(variableType) as any)['x-dependentDictionaries']) {
					variableType = convertJsonSchemaPropertyToEntityProperty(variableName, projectedSchema, true);
				}
				variableType = projectVariableDictionaryKeys(variableType, variable.nameRef, variable.dictionaryKeys, data, inputScope);
				outputScope.setVariable(variableName, { ...variableType, isRequired: true, possiblyUnavailable: false, scopeOrigins: [
					...Array.from(new Set([...(variableType.scopeOrigins ?? []), `${contextName}.${variableName}`])),
				] });
			}
		}
	}

	private async resolveServiceConnectionPropertyJsonSchema(
		schemaSource: NonNullable<VariableSetMetadata['typeJsonSchemaSource']>,
		data: Record<string, any>,
	): Promise<EntityMetadataModel | undefined> {
		const serviceConnectionRef = getModelValueByNameRef(data, schemaSource.serviceConnectionNameRef);
		const serviceConnectionId = getServiceConnectionId(serviceConnectionRef);
		if (!serviceConnectionId || !schemaSource.propertyName) {
			return undefined;
		}

		const dependentValues = Object.fromEntries((schemaSource.dependentValueNameRefs ?? []).map((nameRef) => [nameRef, getModelValueByNameRef(data, nameRef)]));

		if (Object.values(dependentValues).some((value) => value === null || value === undefined || value === '')) {
			return undefined;
		}

		const property = await this.queryClient.fetchQuery(
			this.flowQueries.queryServiceConnectionProperty(serviceConnectionId, schemaSource.propertyName, dependentValues),
		);

		const schema = property?.value as JsonSchemaModel | undefined;
		if (schema?.type !== 'object') {
			return undefined;
		}

		return getEntityMetadataModelFromJsonSchema(schema);
	}

	/**
	 * Processes the expression-level variableSets of a single expression into the given scope.
	 * Uses the two-phase approach (temp scope + mergeMultiSegmentPathContributions) to correctly
	 * handle multi-segment nameRef paths (e.g. ResponseVariable = ["$view", "record"]).
	 *
	 * Only processes top-level expression variableSets (ExpressionMetadataModel.variableSets).
	 * Parameter-level variableSets are intentionally skipped.
	 *
	 * @param expression The expression to process
	 * @param contextScope The outer scope used for type resolution and existing entity lookup
	 * @param outputScope The scope where contributions are written
	 */
	protected async processExpressionVariableSetsInto(
		expression: ExpressionModel,
		contextScope: IVariableScope,
		outputScope: VariableScope,
		getPropertyTypeFromExpression?: (
			expression: ExpressionModel,
			name: string,
			variableScope: IVariableScope,
			variable?: VariableTypeMetadata,
		) => ObjectPropertyType | undefined,
	): Promise<void> {
		const metadata = this.getExpressionMetadata(expression.Type);
		if (!metadata?.variableSets?.length) return;

		const tempScope = new VariableScope();
		await this.processVariableSets(
			metadata.variableSets,
			metadata.parameters,
			expression as Record<string, any>,
			contextScope,
			tempScope,
			`expression '${expression.Type}'`,
			getPropertyTypeFromExpression,
		);

		this.mergeMultiSegmentPathContributions(metadata.variableSets, expression as Record<string, any>, tempScope, contextScope, outputScope);
	}

	/**
	 * Extracts the full Var path from a nameRef value, returning all segments.
	 * Unlike resolveVariableNameFromNameRef (which only returns Value[0]),
	 * this returns the complete path so callers can detect multi-segment assignments
	 * like ["$view", "record"] that should be merged as a nested property.
	 */
	protected resolveNameRefFullPath(nameRefValue: unknown): string[] | undefined {
		if (!nameRefValue) return undefined;
		if (typeof nameRefValue === 'object' && (nameRefValue as ExpressionModel).Type === ExpressionTypes.Var) {
			const varExp = nameRefValue as VarExpressionModel;
			if (Array.isArray(varExp.Value) && varExp.Value.length > 0) {
				return varExp.Value as string[];
			}
		}
		if (typeof nameRefValue === 'string') {
			return [nameRefValue];
		}
		return undefined;
	}

	/**
	 * Post-processes contributions from processVariableSets to handle multi-segment nameRef paths.
	 *
	 * When a variableSet's nameRef resolves to a 2-segment Var path (e.g. ["$view", "noteText"]),
	 * processVariableSets naively uses Value[0] ("$view") as the variable name, which would
	 * overwrite the existing $view object. Instead, the contribution must be merged as a named
	 * property of the existing root variable.
	 *
	 * This method should be called after processVariableSets writes into a temp scope.
	 * It inspects each nameRef-based variableSet for multi-segment paths, merges the contributed
	 * type as a property of the existing root entity, and writes the result into outputScope.
	 * Single-segment (or non-nameRef) contributions are forwarded as-is.
	 *
	 * @param variableSets The variableSets array that was processed
	 * @param data The component/expression data (to resolve nameRef values)
	 * @param tempContributed The temp scope where processVariableSets wrote its output
	 * @param contextScope The outer scope used to look up existing root variable entities
	 * @param outputScope The scope where merged contributions are written
	 */
	protected mergeMultiSegmentPathContributions(
		variableSets: VariableSetMetadata[],
		data: Record<string, any>,
		tempContributed: VariableScope,
		contextScope: IVariableScope,
		outputScope: VariableScope,
	): void {
		const handledNames = new Set<string>();

		for (const variableSet of variableSets) {
			if (!variableSet.nameRef) continue;
			const fullPath = this.resolveNameRefFullPath(data[variableSet.nameRef]);
			if (!fullPath || fullPath.length < 2) continue;

			const rootName = fullPath[0];
			const varType = tempContributed.getVariable(rootName);
			if (!varType) continue;

			handledNames.add(rootName);

			// Get existing root entity or start fresh
			const existingRoot = contextScope.getVariable(rootName);
			const existingEntity = existingRoot?.referenceEntity as EntityMetadataModel | undefined;

			// Build nested structure for path segments
			const mergedEntity = this.mergeNestedPath(
				fullPath.slice(1), // All segments after root
				varType,
				existingEntity,
				rootName,
			);

			outputScope.setVariable(rootName, {
				name: rootName,
				type: PrimitiveType.Object,
				isComplex: true,
				isMultiple: false,
				isRequired: false,
				referenceEntity: mergedEntity,
				referenceEntityId: mergedEntity.id,
			});
		}

		// Forward any contributions that were not handled by multi-segment path merging
		tempContributed.getVariables().forEach((v, name) => {
			if (!handledNames.has(name)) {
				outputScope.setVariable(name, v);
			}
		});
	}

	/**
	 * Recursively builds nested object structures for multi-segment variable paths.
	 * For example, path ["contact", "firstName"] with type String creates:
	 * - contact: Object with properties [firstName: String]
	 */
	private mergeNestedPath(
		pathSegments: string[],
		leafType: ObjectPropertyType,
		existingEntity: EntityMetadataModel | undefined,
		entityId: string,
	): EntityMetadataModel {
		const [propName, ...remainingPath] = pathSegments;

		// Determine property type based on whether this is the leaf or an intermediate segment
		const propType: ObjectPropertyType =
			remainingPath.length === 0
				? this.createLeafProperty(propName, leafType)
				: this.createIntermediateProperty(propName, remainingPath, leafType, existingEntity, entityId);

		return this.mergePropertyIntoEntity(propName, propType, existingEntity, entityId);
	}

	/**
	 * Creates a property descriptor for a leaf segment (gets the actual resolved type).
	 */
	private createLeafProperty(propName: string, leafType: ObjectPropertyType): ObjectPropertyType {
		return {
			name: propName,
			type: leafType.type,
			isComplex: leafType.isComplex,
			isMultiple: leafType.isMultiple,
			isRequired: false,
			referenceEntity: leafType.referenceEntity,
			referenceEntityId: leafType.referenceEntityId,
		};
	}

	/**
	 * Creates a property descriptor for an intermediate segment (becomes an Object type).
	 * Recursively processes remaining path segments.
	 */
	private createIntermediateProperty(
		propName: string,
		remainingPath: string[],
		leafType: ObjectPropertyType,
		existingEntity: EntityMetadataModel | undefined,
		entityId: string,
	): ObjectPropertyType {
		// Find existing nested entity for this property
		const existingProp = existingEntity?.properties?.find((p) => p.name === propName);
		const existingNestedEntity = existingProp?.referenceEntity as EntityMetadataModel | undefined;

		// Recursively build nested structure
		const nestedEntityId = `${entityId}.${propName}`;
		const nestedEntity = this.mergeNestedPath(remainingPath, leafType, existingNestedEntity, nestedEntityId);

		return {
			name: propName,
			type: PrimitiveType.Object,
			isComplex: true,
			isMultiple: false,
			isRequired: false,
			referenceEntity: nestedEntity,
			referenceEntityId: nestedEntity.id,
		};
	}

	/**
	 * Merges a property into an entity, preserving existing properties and metadata.
	 */
	private mergePropertyIntoEntity(
		propName: string,
		propType: ObjectPropertyType,
		existingEntity: EntityMetadataModel | undefined,
		entityId: string,
	): EntityMetadataModel {
		return {
			id: existingEntity?.id ?? entityId,
			contextId: existingEntity?.contextId ?? '',
			name: existingEntity?.name ?? entityId,
			description: existingEntity?.description ?? '',
			properties: [...(existingEntity?.properties ?? []).filter((p) => p.name !== propName), propType],
		};
	}
}
